
import { useId, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { OPERATING_LOOP_TABS, TAB_ICONS } from "./operating-loop/tabs-data";
import type { TabId } from "./operating-loop/tabs-data";
import { MapVisual } from "./operating-loop/map-visual";
import { IdentifyVisual } from "./operating-loop/identify-visual";
import { ReviewVisual } from "./operating-loop/review-visual";
import { MonitorVisual } from "./operating-loop/monitor-visual";
import { DemonstrateVisual } from "./operating-loop/demonstrate-visual";

const VISUALS: Record<TabId, ReactNode> = {
  map: <MapVisual />,
  identify: <IdentifyVisual />,
  review: <ReviewVisual />,
  monitor: <MonitorVisual />,
  demonstrate: <DemonstrateVisual />,
};

export function OperatingLoopTabs() {
  const [activeId, setActiveId] = useState<TabId>("map");
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  // OPERATING_LOOP_TABS is a non-empty const array; [0] is always defined.
  const active = OPERATING_LOOP_TABS.find((tab) => tab.id === activeId) ?? OPERATING_LOOP_TABS[0]!;
  const activeIndex = OPERATING_LOOP_TABS.findIndex((tab) => tab.id === active.id);

  function focusTab(nextIndex: number) {
    // nextIndex is always modulo-bounded by OPERATING_LOOP_TABS.length; always defined.
    const next = OPERATING_LOOP_TABS[nextIndex]!;
    setActiveId(next.id);
    requestAnimationFrame(() => {
      document.getElementById(`${baseId}-${next.id}-tab`)?.focus();
    });
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusTab((activeIndex + 1) % OPERATING_LOOP_TABS.length);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusTab((activeIndex - 1 + OPERATING_LOOP_TABS.length) % OPERATING_LOOP_TABS.length);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusTab(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusTab(OPERATING_LOOP_TABS.length - 1);
    }
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="-mx-6 overflow-x-auto border-y border-border bg-surface px-6 sm:mx-0 sm:rounded-t-lg sm:border sm:px-0">
        <div
          role="tablist"
          aria-label="Operating loop capabilities"
          className="flex min-w-max snap-x sm:min-w-0"
        >
          {OPERATING_LOOP_TABS.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            const selected = tab.id === activeId;

            return (
              <button
                key={tab.id}
                id={`${baseId}-${tab.id}-tab`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={panelId}
                tabIndex={selected ? 0 : -1}
                onClick={() =>
                  focusTab(OPERATING_LOOP_TABS.findIndex((t) => t.id === tab.id))
                }
                onKeyDown={handleTabKeyDown}
                className={cn(
                  "relative flex min-h-13 min-w-[7.5rem] snap-start items-center justify-center gap-2 border-r border-border px-4 py-3 text-sm font-semibold transition-colors duration-200 last:border-r-0 sm:min-w-[8.75rem] sm:flex-1 sm:px-5 sm:py-4",
                  selected
                    ? "bg-primary-tint text-primary"
                    : "text-foreground-muted hover:bg-secondary hover:text-foreground",
                )}
              >
                <Icon className="size-5" aria-hidden="true" />
                {tab.label}
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-x-5 bottom-0 h-0.5 origin-center bg-primary transition-transform duration-200 sm:inset-x-6",
                    selected ? "scale-x-100" : "scale-x-0",
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${baseId}-${active.id}-tab`}
        className="grid overflow-hidden border-b border-border bg-surface shadow-sm sm:rounded-b-lg sm:border-x md:grid-cols-[0.34fr_0.66fr] lg:grid-cols-[0.38fr_0.62fr]"
      >
        <div className="border-b border-border p-5 sm:p-6 md:border-r md:border-b-0 lg:p-8">
          <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.08em] text-primary">
            {active.eyebrow}
          </p>
          <h3 className="mt-3 max-w-md text-[1.35rem] font-semibold leading-tight tracking-[-0.02em] text-foreground sm:text-2xl md:mt-4">
            {active.heading}
          </h3>
          <p className="mt-3 max-w-md text-base leading-relaxed text-foreground-muted md:mt-4">
            {active.description}
          </p>
        </div>
        <div key={active.id} className="animate-in fade-in slide-in-from-bottom-1 duration-200 p-4 sm:p-6 lg:p-7">
          {VISUALS[active.id]}
        </div>
      </div>

      <p className="mx-auto mt-4 max-w-3xl text-center text-sm leading-relaxed text-foreground-muted sm:mt-5">
        Truspecta models privacy at the workload level, where your systems actually live.
      </p>
    </div>
  );
}
